<div class="trade-offer-page">
  <div class="page-header d-print-none">
    <app-page-title [title]="pageTitle()"></app-page-title>
  </div>

  @if (loading()) {
    <div class="trade-empty">กำลังโหลด…</div>
  } @else if (notFound() || !offer()) {
    <div class="alert alert-warning d-flex flex-wrap align-items-center gap-2" role="alert">
      <span>ไม่พบใบขายนี้</span>
      <a class="fw-semibold" routerLink="/trade">กลับไปรายการซื้อ-ขาย</a>
    </div>
  } @else if (offer(); as current) {
    <div class="invoice-container">
      <div class="invoice-card ticket-card">

        <!-- Top Header: the buyer sees their purchase, the seller sees their ใบขาย -->
        <div class="invoice-header">
          <div class="d-flex align-items-center gap-3">
            <div class="invoice-brand-icon">
              @if (isBuyer()) {
                <img src="/assets/images/empty-basket.png" alt="" class="invoice-cart-img">
              } @else {
                <img src="assets/images/icons/shopping-cart.png" alt="Cart" class="invoice-cart-img">
              }
            </div>
            <div>
              <div class="invoice-title-row">
                <h4 class="mb-0 fw-bold text-dark invoice-main-title">
                  @if (isBuyer()) {
                    รับซื้อจาก {{ current.businessTitle || 'ผู้ขาย' }}
                  } @else {
                    ใบขาย -
                    @if (productNames(); as prodName) {
                      {{ prodName }}
                    }
                  }
                </h4>
                @if (isBuyer()) {
                  <span class="badge text-bg-success" id="offer-buyer-status">รับแล้ว</span>
                }
              </div>
              @if (isBuyer()) {
                <div class="text-secondary small mt-1">
                  @if (productNames(); as prodName) {
                    {{ prodName }} ·
                  }
                  รับเมื่อ {{ current.acceptedAt | thaidatetime }}
                </div>
              } @else {
                <div class="text-secondary small mt-1">{{ current.createdAt | thaidatetime }}</div>
              }
            </div>
          </div>
        </div>

        <!-- Transaction Meta Key-Value List -->
        <div class="invoice-meta-list">
          <!-- While the offer is open the code sits under the QR (work item 178: one place only). -->
          @if (!isOpen()) {
            <div class="invoice-meta-row" id="offer-code-row">
              <span class="meta-label">{{ isBuyer() ? 'ใบขายของผู้ขาย' : 'รหัสใบขาย' }}</span>
              <div class="meta-value d-flex align-items-center justify-content-end gap-2">
                <app-record-code [code]="current.offerCode" variant="badge"
                                 copyTitle="คัดลอกรหัสใบขาย"
                                 toastMessage="คัดลอกรหัสใบขายแล้ว"></app-record-code>
              </div>
            </div>
          }

          @if (isBuyer()) {
            <div class="invoice-meta-row">
              <span class="meta-label">รับเข้าธุรกิจ</span>
              <div class="meta-value fw-semibold text-dark">
                {{ current.acceptedBusinessTitle || '-' }}
                <span class="badge rounded-pill text-bg-light border ms-1 you-badge">คุณ</span>
              </div>
            </div>
            <div class="invoice-meta-row">
              <span class="meta-label">วันที่รับ</span>
              <div class="meta-value text-secondary">
                {{ current.acceptedAt | thaidatetime }}
              </div>
            </div>
          }

          <div class="invoice-meta-row">
            <span class="meta-label">{{ isBuyer() ? 'ผู้ขาย' : 'ธุรกิจผู้ขาย' }}</span>
            <div class="meta-value fw-semibold text-dark">
              {{ current.businessTitle || '-' }}
              @if (isSeller()) {
                <span class="badge rounded-pill text-bg-light border ms-1 you-badge">คุณ</span>
              }
            </div>
          </div>

          <div class="invoice-meta-row">
            <span class="meta-label">{{ isBuyer() ? 'ผู้ขายสร้างใบขายเมื่อ' : 'วันที่สร้าง' }}</span>
            <div class="meta-value text-secondary">
              {{ current.createdAt | thaidatetime }}
            </div>
          </div>

          @if (current.note) {
            <div class="invoice-meta-row">
              <span class="meta-label">หมายเหตุ</span>
              <div class="meta-value text-secondary">
                {{ current.note }}
              </div>
            </div>
          }

          @if (current.status === 'ACCEPTED' && !isBuyer()) {
            <div class="invoice-meta-row">
              <span class="meta-label">ผู้รับสินค้า</span>
              <div class="meta-value fw-semibold text-dark">
                {{ current.acceptedBusinessTitle || '-' }}
              </div>
            </div>
            <div class="invoice-meta-row">
              <span class="meta-label">วันที่รับ</span>
              <div class="meta-value text-secondary">
                {{ current.acceptedAt | thaidatetime }}
              </div>
            </div>
          }

          @if (current.status === 'CANCELLED') {
            <div class="invoice-meta-row">
              <span class="meta-label">วันที่ยกเลิก</span>
              <div class="meta-value text-danger fw-semibold">
                {{ current.cancelledAt | thaidatetime }}
              </div>
            </div>
          }
        </div>

        <!-- Ticket middle notch line between meta list and cart -->
        <div class="ticket-divider-line"></div>

        <!-- Product Items Section -->
        <div class="invoice-cart-section">
          <div class="section-heading mb-2 fw-semibold text-secondary small">สินค้า</div>

          <div class="cart-items-list">
            @for (item of items(); track item.id; let idx = $index) {
              <div class="invoice-meta-row cart-item-row">
                <div class="meta-label cart-product-info">
                  <span class="cart-item-num me-2 text-muted">{{ idx + 1 }}</span>
                  <span class="cart-item-name text-dark fw-semibold">{{ item.productName || '-' }}</span>
                  @if (offerComponentsBreakdown(item.components); as breakdown) {
                    <div class="cart-item-components text-muted small ms-4">{{ breakdown }}</div>
                  }
                  @if (item.acceptedRecordId != null) {
                    <div class="ms-2 mt-1">
                      <a class="view-item-link small" [routerLink]="[tradeBase, item.acceptedRecordId]">
                        {{ isBuyer() ? 'ดูรายการรับซื้อของคุณ' : 'ดูรายการ' }} <i class="bi bi-arrow-right"></i>
                      </a>
                    </div>
                  }
                </div>
                <div class="meta-value cart-item-qty fw-bold text-dark">
                  @if (takenLess(item)) {
                    <div>{{ formatQuantityWithUnit(item.acceptedQuantity, unit) }}</div>
                    <div class="text-secondary small fw-normal">จากใบขาย {{ formatQuantityWithUnit(item.quantity, unit) }}</div>
                  } @else {
                    {{ formatQuantityWithUnit(item.quantity, unit) }}
                  }
                </div>
              </div>
            }
          </div>

          <!-- Total Quantity Row -->
          <div class="invoice-meta-row total-highlight-row">
            <span class="meta-label total-label fw-bold text-dark">รวมทั้งใบ</span>
            <span class="meta-value total-value fw-bold text-dark" id="offer-total-quantity">
              @if (partiallyAccepted()) {
                {{ formatQuantityWithUnit(acceptedTotalQuantity(), unit) }}
                <span class="text-secondary fw-normal small ms-1" id="offer-offered-total">
                  จากใบขาย {{ formatQuantityWithUnit(totalQuantity(), unit) }}
                </span>
              } @else {
                {{ formatQuantityWithUnit(totalQuantity(), unit) }}
              }
            </span>
          </div>

          @if (current.acceptedBusinessTitle && !isBuyer()) {
            <div class="cart-accepted-info text-muted small mt-2 text-end">
              <span>ผู้รับสินค้า:</span>
              <strong class="text-dark ms-1">{{ current.acceptedBusinessTitle }}</strong>
              @if (current.acceptedAt) {
                <span class="ms-1">({{ current.acceptedAt | thaidatetime }})</span>
              }
            </div>
          }
        </div>

        <!-- QR Code Section for Accepting Trade -->
        @if (isOpen()) {
          <div class="invoice-qr-stub">
            <aside class="offer-qr-section" id="offer-qr" aria-label="QR ใบขาย">
              <div class="qr-code-frame">
                @if (qrImageUrl(); as qrUrl) {
                  <img [src]="qrUrl" class="offer-qr-img" id="offer-qr-img"
                       alt="QR ใบขาย {{ current.offerCode }}">
                } @else {
                  <div class="offer-qr-img offer-qr-empty">
                    <i class="bi bi-qr-code" aria-hidden="true"></i>
                  </div>
                }
              </div>
              <div class="qr-scan-hint text-secondary small mt-2">ให้ผู้ซื้อสแกนเพื่อรับสินค้าตามจำนวนในใบขาย</div>
              <div class="offer-qr-code-row">
                <code class="offer-qr-code-text" id="offer-qr-code">{{ current.offerCode }}</code>
                <button type="button" class="offer-qr-copy-button" id="offer-qr-copy"
                        (click)="copyOfferCode()"
                        [attr.aria-label]="codeCopied() ? 'คัดลอกแล้ว' : 'คัดลอกรหัสใบขาย'"
                        [title]="codeCopied() ? 'คัดลอกแล้ว' : 'คัดลอกรหัสใบขาย'">
                  @if (codeCopied()) {
                    <i class="bi bi-check2 text-success" aria-hidden="true"></i>
                    <span>คัดลอกแล้ว</span>
                  } @else {
                    <i class="bi bi-copy" aria-hidden="true"></i>
                    <span>คัดลอกรหัส</span>
                  }
                </button>
              </div>
            </aside>
          </div>
        }

        <!-- Card Action Buttons -->
        <div class="invoice-actions d-print-none">
          @if (isOpen() && qrImageUrl()) {
            <button type="button" class="btn btn-outline-secondary btn-sm" id="offer-qr-download"
                    (click)="downloadQr()">
              <i class="bi bi-download" aria-hidden="true"></i> บันทึกรูป QR Code
            </button>
          }
        </div>

        <!-- Ticket Scalloped Bottom Edge (Sawtooth / wave cut bottom edge) -->
        <div class="ticket-bottom-scallop" aria-hidden="true"></div>

      </div>

      <!-- Bottom toolbar under the card -->
      <div class="invoice-bottom-toolbar d-print-none">
        @if (isBuyer()) {
          @if (soleAcceptedRecordId(); as recordId) {
            <a class="btn btn-primary btn-sm" id="offer-buyer-record" [routerLink]="[tradeBase, recordId]">
              <i class="bi bi-box-seam me-1"></i> ดูรายการรับซื้อของคุณ
            </a>
          }
          <a class="btn btn-outline-secondary btn-sm" id="offer-buyer-stock" routerLink="/trade">
            <i class="bi bi-boxes me-1"></i> ไปที่สต็อก
          </a>
        }
        @if (current.canCancel) {
          <button type="button" class="btn btn-outline-danger btn-sm" [disabled]="cancelling()"
                  (click)="cancelOffer()">
            @if (cancelling()) {
              <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            }
            <i class="bi bi-x-circle me-1"></i> ยกเลิกใบขาย
          </button>
        }
        <a class="btn btn-link ms-auto text-decoration-none" routerLink="/trade">
          <i class="bi bi-chevron-left me-1"></i> กลับไปรายการ
        </a>
      </div>
    </div>
  }
</div>
